// Billing helpers — Razorpay checkout integration + currency helpers + global 402 dispatch.
// Backend exposes /api/billing/plans (public), /api/billing/me (auth), /api/billing/checkout/one-time,
// /api/billing/checkout/gold, /api/billing/verify-payment. We keep API methods here so other UI
// modules import only from one place.

import api from './api-axios';
import { toast } from 'sonner';
import { trackPaymentEvent } from './paymentTracker';

// ---- API ----
export const billingApi = {
  plans: () => api.get('/billing/plans').then((r) => r.data),
  me: () => api.get('/billing/me').then((r) => r.data),
  checkoutOneTime: () => api.post('/billing/checkout/one-time').then((r) => r.data),
  checkoutOneTimeGuest: (body) =>
    api.post('/billing/checkout/one-time/guest', body).then((r) => r.data),
  checkoutExpertAuditGuest: (body) =>
    api.post('/billing/checkout/expert-audit/guest', body).then((r) => r.data),
  checkoutGold: () => api.post('/billing/checkout/gold').then((r) => r.data),
  verifyPayment: (payload) => api.post('/billing/verify-payment', payload).then((r) => r.data),
  cancelSubscription: (payload) =>
    api.post('/billing/cancel', payload || { cancel_at_cycle_end: true }).then((r) => r.data),
};

// ---- Currency helpers ----
// We charge in INR only for now but display USD for non-IN visitors. Manual override
// is persisted in localStorage so power users on Indian VPNs can switch easily.
//
// Detection priority (from most authoritative to least):
//   1. Explicit user override stored in localStorage    (persistCurrency)
//   2. Server-side IP geolocation via /api/geo/country  (fetchServerCurrency)
//   3. Browser heuristics (timezone / language)         (detectCountry)
//
// NB: The v1 storage key ('execpresence.currency') was auto-written by
// `HomeHero` on every visit which meant a returning user's currency was
// permanently locked to their FIRST-visit country — VPN switches were
// invisible on subsequent loads. That bug is fixed (auto-detect no
// longer writes to the override key), but we also renamed the key to
// v2 so returning users get a clean slate.
const CURRENCY_KEY = 'execpresence.currency.v2';
const LEGACY_CURRENCY_KEY = 'execpresence.currency';
const GEOIP_CACHE_KEY = 'execpresence.geoipCurrency';
// 1h — short enough that a genuine VPN change picks up quickly on the
// same session; long enough to avoid hammering the backend on every
// nav. Backend has its own 24h per-IP cache so the network cost stays
// low regardless.
const GEOIP_CACHE_TTL_MS = 60 * 60 * 1000;

// Best-effort one-time migration: wipe the legacy auto-detect value +
// stale geoip cache so pre-fix visitors are not stuck on the wrong
// currency. Safe to no-op in SSR / when localStorage is unavailable.
try {
  if (typeof window !== 'undefined') {
    if (window.localStorage.getItem(LEGACY_CURRENCY_KEY)) {
      window.localStorage.removeItem(LEGACY_CURRENCY_KEY);
      window.localStorage.removeItem(GEOIP_CACHE_KEY);
    }
  }
} catch (_e) {
  /* ignore */
}

function detectCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.toLowerCase().includes('kolkata') || tz.toLowerCase().includes('calcutta')) return 'IN';
    const lang = (navigator.language || '').toLowerCase();
    if (lang.endsWith('-in') || lang === 'hi') return 'IN';
  } catch (_e) {
    /* ignore */
  }
  return 'OTHER';
}

export function detectCurrency() {
  if (typeof window === 'undefined') return 'USD';
  const stored = window.localStorage.getItem(CURRENCY_KEY);
  if (stored === 'INR' || stored === 'USD') return stored;
  // Second-best: server-detected currency cached from a previous page load.
  try {
    const cached = window.localStorage.getItem(GEOIP_CACHE_KEY);
    if (cached) {
      const rec = JSON.parse(cached);
      if (rec?.currency && rec?.ts && Date.now() - rec.ts < GEOIP_CACHE_TTL_MS) {
        if (rec.currency === 'INR' || rec.currency === 'USD') return rec.currency;
      }
    }
  } catch (_e) {
    /* ignore */
  }
  return detectCountry() === 'IN' ? 'INR' : 'USD';
}

/**
 * Fetch the caller's currency + country. Reads a fresh (24h) cached result
 * from localStorage first; only hits `/api/geo/country` when the cache is
 * stale or empty. Result is cached for 24h so repeat visits don't re-fetch.
 *
 * Returns { currency, country } or null when the caller's location can't
 * be determined (private IP, geoip provider down, etc.).
 *
 * The caller (pricing pages) should call this on mount; when the returned
 * currency / country differs from initial state, update the UI so the tab
 * shown reflects the server's authoritative view.
 */
export async function fetchServerCurrency() {
  if (typeof window === 'undefined') return null;
  // 1) Fresh cache wins — no network needed.
  try {
    const cached = window.localStorage.getItem(GEOIP_CACHE_KEY);
    if (cached) {
      const rec = JSON.parse(cached);
      if (
        rec?.ts &&
        Date.now() - rec.ts < GEOIP_CACHE_TTL_MS &&
        (rec.currency === 'INR' || rec.currency === 'USD')
      ) {
        return { currency: rec.currency, country: rec.country || '' };
      }
    }
  } catch (_e) {
    /* ignore */
  }
  // 2) Cache miss / stale → hit the backend.
  try {
    const res = await api.get('/geo/country', { timeout: 4000 });
    const cur = res?.data?.currency;
    const country = res?.data?.country || '';
    if (cur === 'INR' || cur === 'USD') {
      window.localStorage.setItem(
        GEOIP_CACHE_KEY,
        JSON.stringify({ currency: cur, country, ts: Date.now() }),
      );
      return { currency: cur, country };
    }
  } catch (_e) {
    /* silent — pricing will fall back to browser heuristic */
  }
  return null;
}

/**
 * Read the cached geoip country (ISO-2 code, e.g. 'IN' / 'US'). Returns
 * empty string when we've never resolved the caller's country. Used
 * synchronously by pricing UIs to decide whether to hide the INR or USD tab
 * on first paint (before fetchServerCurrency() completes).
 */
export function cachedGeoCountry() {
  if (typeof window === 'undefined') return '';
  try {
    const cached = window.localStorage.getItem(GEOIP_CACHE_KEY);
    if (!cached) return '';
    const rec = JSON.parse(cached);
    if (!rec?.ts || Date.now() - rec.ts > GEOIP_CACHE_TTL_MS) return '';
    return rec.country || '';
  } catch (_e) {
    return '';
  }
}

export function persistCurrency(curr) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(CURRENCY_KEY, curr);
  }
}

// ---- Pending audit persistence ----
// When a user hits a 402 at audit submit, we stash their fully-filled wizard
// state in localStorage so they can resume right after paying. State expires
// after 1 hour to avoid stale-data confusion when users come back later.
const PENDING_AUDIT_KEY = 'execpresence.pendingAudit';
const PENDING_TTL_MS = 60 * 60 * 1000; // 1h

export function persistPendingAudit(payload) {
  if (typeof window === 'undefined') return;
  try {
    const record = { ...payload, ts: Date.now() };
    window.localStorage.setItem(PENDING_AUDIT_KEY, JSON.stringify(record));
  } catch (_e) {
    /* quota or serialization failure — silent */
  }
}

export function readPendingAudit() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PENDING_AUDIT_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (!record || typeof record !== 'object') return null;
    if (!record.ts || Date.now() - record.ts > PENDING_TTL_MS) {
      window.localStorage.removeItem(PENDING_AUDIT_KEY);
      return null;
    }
    return record;
  } catch (_e) {
    return null;
  }
}

export function clearPendingAudit() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PENDING_AUDIT_KEY);
  } catch (_e) {
    /* ignore */
  }
}

export function hasPendingAudit() {
  return readPendingAudit() !== null;
}

export function formatPrice(amount, currency) {
  if (amount === null || amount === undefined) return '\u2014';
  if (currency === 'INR') return `\u20b9${amount.toLocaleString('en-IN')}`;
  return `$${amount}`;
}

// ---- Razorpay loader ----
let razorpayLoaderPromise = null;
export function loadRazorpayScript() {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayLoaderPromise) return razorpayLoaderPromise;
  razorpayLoaderPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return razorpayLoaderPromise;
}

// Open Razorpay Checkout for a Profile Success one-time order.
export async function openOneTimeCheckout({ onSuccess, onDismiss, ctaLabel } = {}) {
  trackPaymentEvent({ event_type: 'click', plan_type: 'one_time', cta_label: ctaLabel || 'one_time_authed' });
  const ok = await loadRazorpayScript();
  if (!ok) {
    toast.error('Could not load Razorpay. Check your network and retry.');
    trackPaymentEvent({ event_type: 'checkout_failed', plan_type: 'one_time', reason: 'razorpay_script_load_failed' });
    return;
  }
  let payload;
  try {
    payload = await billingApi.checkoutOneTime();
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not start checkout');
    trackPaymentEvent({ event_type: 'checkout_failed', plan_type: 'one_time', reason: `create_order_failed:${e?.response?.status || 'net'}` });
    return;
  }
  let completed = false;
  trackPaymentEvent({
    event_type: 'checkout_started',
    plan_type: 'one_time',
    razorpay_order_id: payload.order_id,
    amount_paise: payload.amount,
    currency: payload.currency,
  });
  const rzp = new window.Razorpay({
    key: payload.key_id,
    order_id: payload.order_id,
    amount: payload.amount,
    currency: payload.currency,
    name: payload.name,
    description: payload.description,
    prefill: payload.prefill,
    notes: payload.notes,
    theme: { color: '#0B5FAD' },
    modal: {
      ondismiss: () => {
        if (!completed) {
          trackPaymentEvent({
            event_type: 'checkout_abandoned',
            plan_type: 'one_time',
            razorpay_order_id: payload.order_id,
            amount_paise: payload.amount,
            currency: payload.currency,
            reason: 'user_dismissed_modal',
          });
        }
        onDismiss?.();
      },
    },
    handler: async (resp) => {
      completed = true;
      trackPaymentEvent({
        event_type: 'checkout_completed',
        plan_type: 'one_time',
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        amount_paise: payload.amount,
        currency: payload.currency,
      });
      try {
        const data = await billingApi.verifyPayment({
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        });
        toast.success('Payment successful \u2014 Profile Success Plan activated');
        onSuccess?.({
          ...data,
          plan: 'one_time',
          currency: payload.currency,
          amount_paise: payload.amount,
          order_id: resp.razorpay_order_id,
          payment_id: resp.razorpay_payment_id,
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Payment verification failed');
      }
    },
  });
  rzp.on('payment.failed', (resp) => {
    completed = true; // suppress abandonment
    toast.error('Payment failed. No charge was made.');
    trackPaymentEvent({
      event_type: 'checkout_failed',
      plan_type: 'one_time',
      razorpay_order_id: payload.order_id,
      reason: (resp?.error?.description || 'payment_failed').slice(0, 120),
    });
  });
  rzp.open();
}

/**
 * Open Razorpay Checkout for a **guest** Profile Success one-time payment.
 *
 * Unlike ``openOneTimeCheckout`` (authenticated flow) this variant:
 *   - Accepts email/name/phone upfront (no session required).
 *   - Hits ``POST /billing/checkout/one-time/guest`` to mint a Razorpay Order.
 *   - Opens Razorpay's SDK Checkout modal (which has a proper X close button
 *     unlike the hosted Payment Button whose iframe has no visible dismiss).
 *
 * Account provisioning is fully asynchronous — the ``payment.captured``
 * webhook creates the Blayzrr user + activates the plan by email. On the
 * frontend we just show a "check your inbox" toast + redirect to the login
 * page after the modal closes on success.
 */
export async function openOneTimeCheckoutGuest({ email, name, phone, linkedin_url, currency, onSuccess, onDismiss, ctaLabel } = {}) {
  trackPaymentEvent({
    event_type: 'click',
    plan_type: 'one_time',
    cta_label: ctaLabel || 'one_time_guest',
    guest_email: email,
    guest_name: name,
  });
  const ok = await loadRazorpayScript();
  if (!ok) {
    toast.error('Could not load Razorpay. Check your network and retry.');
    trackPaymentEvent({ event_type: 'checkout_failed', plan_type: 'one_time', reason: 'razorpay_script_load_failed', guest_email: email });
    return;
  }
  let payload;
  try {
    payload = await billingApi.checkoutOneTimeGuest({ email, name, phone, linkedin_url, currency });
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not start checkout');
    trackPaymentEvent({ event_type: 'checkout_failed', plan_type: 'one_time', reason: `create_order_failed:${e?.response?.status || 'net'}`, guest_email: email });
    return;
  }
  let completed = false;
  trackPaymentEvent({
    event_type: 'checkout_started',
    plan_type: 'one_time',
    guest_email: email,
    guest_name: name,
    razorpay_order_id: payload.order_id,
    amount_paise: payload.amount,
    currency: payload.currency,
  });
  const rzp = new window.Razorpay({
    key: payload.key_id,
    order_id: payload.order_id,
    amount: payload.amount,
    currency: payload.currency,
    name: payload.name,
    description: payload.description,
    prefill: payload.prefill,
    notes: payload.notes,
    theme: { color: '#0B5FAD' },
    modal: {
      ondismiss: () => {
        if (!completed) {
          trackPaymentEvent({
            event_type: 'checkout_abandoned',
            plan_type: 'one_time',
            guest_email: email,
            guest_name: name,
            razorpay_order_id: payload.order_id,
            amount_paise: payload.amount,
            currency: payload.currency,
            reason: 'user_dismissed_modal',
          });
        }
        onDismiss?.();
      },
    },
    handler: async (resp) => {
      completed = true;
      trackPaymentEvent({
        event_type: 'checkout_completed',
        plan_type: 'one_time',
        guest_email: email,
        guest_name: name,
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        amount_paise: payload.amount,
        currency: payload.currency,
      });
      toast.success('Payment received! Check your email for login credentials.');
      onSuccess?.({
        payment_id: resp.razorpay_payment_id,
        order_id: resp.razorpay_order_id,
        plan: 'one_time',
        currency: payload.currency,
        amount_paise: payload.amount,
        email,
      });
    },
  });
  rzp.on('payment.failed', (resp) => {
    completed = true;
    toast.error('Payment failed. No charge was made.');
    trackPaymentEvent({
      event_type: 'checkout_failed',
      plan_type: 'one_time',
      guest_email: email,
      razorpay_order_id: payload.order_id,
      reason: (resp?.error?.description || 'payment_failed').slice(0, 120),
    });
  });
  rzp.open();
}

// Open Razorpay Checkout for a GUEST Expert-Audited Profile purchase.
//
// Same JS-SDK modal UX as ``openOneTimeCheckoutGuest`` but the buyer
// gets NO on-platform account — the deliverable is 2 human-written
// PDFs shipped in 4 working hours (see backend
// ``_handle_hosted_button_payment`` expert_audit branch).
export async function openExpertAuditCheckoutGuest({ email, name, phone, linkedin_url, currency, onSuccess, onDismiss, ctaLabel } = {}) {
  trackPaymentEvent({
    event_type: 'click',
    plan_type: 'expert_audit',
    cta_label: ctaLabel || 'expert_audit_guest',
    guest_email: email,
    guest_name: name,
  });
  const ok = await loadRazorpayScript();
  if (!ok) {
    toast.error('Could not load Razorpay. Check your network and retry.');
    trackPaymentEvent({ event_type: 'checkout_failed', plan_type: 'expert_audit', reason: 'razorpay_script_load_failed', guest_email: email });
    return;
  }
  let payload;
  try {
    payload = await billingApi.checkoutExpertAuditGuest({ email, name, phone, linkedin_url, currency });
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not start checkout');
    trackPaymentEvent({ event_type: 'checkout_failed', plan_type: 'expert_audit', reason: `create_order_failed:${e?.response?.status || 'net'}`, guest_email: email });
    return;
  }
  let completed = false;
  trackPaymentEvent({
    event_type: 'checkout_started',
    plan_type: 'expert_audit',
    guest_email: email,
    guest_name: name,
    razorpay_order_id: payload.order_id,
    amount_paise: payload.amount,
    currency: payload.currency,
  });
  const rzp = new window.Razorpay({
    key: payload.key_id,
    order_id: payload.order_id,
    amount: payload.amount,
    currency: payload.currency,
    name: payload.name,
    description: payload.description,
    prefill: payload.prefill,
    notes: payload.notes,
    theme: { color: '#0B5FAD' },
    modal: {
      ondismiss: () => {
        if (!completed) {
          trackPaymentEvent({
            event_type: 'checkout_abandoned',
            plan_type: 'expert_audit',
            guest_email: email,
            guest_name: name,
            razorpay_order_id: payload.order_id,
            amount_paise: payload.amount,
            currency: payload.currency,
            reason: 'user_dismissed_modal',
          });
        }
        onDismiss?.();
      },
    },
    handler: async (resp) => {
      completed = true;
      trackPaymentEvent({
        event_type: 'checkout_completed',
        plan_type: 'expert_audit',
        guest_email: email,
        guest_name: name,
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        amount_paise: payload.amount,
        currency: payload.currency,
      });
      toast.success('Payment received! Our expert will be in touch shortly — check your inbox for the confirmation.');
      onSuccess?.({
        payment_id: resp.razorpay_payment_id,
        order_id: resp.razorpay_order_id,
        plan: 'expert_audit',
        currency: payload.currency,
        amount_paise: payload.amount,
        email,
      });
    },
  });
  rzp.on('payment.failed', (resp) => {
    completed = true;
    toast.error('Payment failed. No charge was made.');
    trackPaymentEvent({
      event_type: 'checkout_failed',
      plan_type: 'expert_audit',
      guest_email: email,
      razorpay_order_id: payload.order_id,
      reason: (resp?.error?.description || 'payment_failed').slice(0, 120),
    });
  });
  rzp.open();
}


// Open Razorpay Checkout for a Gold subscription.
export async function openGoldCheckout({ onSuccess, onDismiss, ctaLabel } = {}) {
  trackPaymentEvent({ event_type: 'click', plan_type: 'gold', cta_label: ctaLabel || 'gold_authed' });
  const ok = await loadRazorpayScript();
  if (!ok) {
    toast.error('Could not load Razorpay. Check your network and retry.');
    trackPaymentEvent({ event_type: 'checkout_failed', plan_type: 'gold', reason: 'razorpay_script_load_failed' });
    return;
  }
  let payload;
  try {
    payload = await billingApi.checkoutGold();
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Could not start checkout');
    trackPaymentEvent({ event_type: 'checkout_failed', plan_type: 'gold', reason: `create_subscription_failed:${e?.response?.status || 'net'}` });
    return;
  }
  let completed = false;
  trackPaymentEvent({
    event_type: 'checkout_started',
    plan_type: 'gold',
    razorpay_subscription_id: payload.subscription_id,
  });
  const rzp = new window.Razorpay({
    key: payload.key_id,
    subscription_id: payload.subscription_id,
    name: payload.name,
    description: payload.description,
    prefill: payload.prefill,
    notes: payload.notes,
    theme: { color: '#0B5FAD' },
    modal: {
      ondismiss: () => {
        if (!completed) {
          trackPaymentEvent({
            event_type: 'checkout_abandoned',
            plan_type: 'gold',
            razorpay_subscription_id: payload.subscription_id,
            reason: 'user_dismissed_modal',
          });
        }
        onDismiss?.();
      },
    },
    handler: async (resp) => {
      completed = true;
      trackPaymentEvent({
        event_type: 'checkout_completed',
        plan_type: 'gold',
        razorpay_subscription_id: resp.razorpay_subscription_id,
        razorpay_payment_id: resp.razorpay_payment_id,
      });
      try {
        const data = await billingApi.verifyPayment({
          razorpay_subscription_id: resp.razorpay_subscription_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        });
        toast.success('Subscribed \u2014 Executive Thought Leadership Plan active');
        onSuccess?.({
          ...data,
          plan: 'gold',
          currency: (payload && payload.currency) || undefined,
          subscription_id: resp.razorpay_subscription_id,
          payment_id: resp.razorpay_payment_id,
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Payment verification failed');
      }
    },
  });
  rzp.on('payment.failed', (resp) => {
    completed = true;
    toast.error('Payment failed. No charge was made.');
    trackPaymentEvent({
      event_type: 'checkout_failed',
      plan_type: 'gold',
      razorpay_subscription_id: payload.subscription_id,
      reason: (resp?.error?.description || 'payment_failed').slice(0, 120),
    });
  });
  rzp.open();
}

// ---- Global 402 dispatcher ----
// A lightweight pub-sub so the global QuotaExceededDialog can react to 402s anywhere in the app.
const quotaListeners = new Set();
export function onQuotaExceeded(listener) {
  quotaListeners.add(listener);
  return () => quotaListeners.delete(listener);
}
export function dispatchQuotaExceeded(detail) {
  quotaListeners.forEach((fn) => {
    try { fn(detail); } catch (_e) { /* ignore */ }
  });
}
